/**
 * Location change helper (item 116).
 * Updates Functions cart attributes, warns when cart lines fail province
 * compliance, keeps lines, then full-reloads (or submits remap form).
 *
 * Also registers <location-selector> for select-based UIs.
 */
(function () {
  const LOCATION_GID_PREFIX = 'gid://shopify/Location/';

  // Cart / Functions / pricing JSON expect two-letter codes (ON), not full names.
  const PROVINCE_NAME_TO_CODE = {
    Alberta: 'AB',
    'British Columbia': 'BC',
    Manitoba: 'MB',
    'New Brunswick': 'NB',
    'Newfoundland and Labrador': 'NL',
    'Nova Scotia': 'NS',
    Ontario: 'ON',
    'Prince Edward Island': 'PE',
    Quebec: 'QC',
    Québec: 'QC',
    Saskatchewan: 'SK',
  };

  const PROVINCE_CODE_ALIASES = {
    AB: 'AB',
    BC: 'BC',
    MB: 'MB',
    NB: 'NB',
    NL: 'NL',
    NS: 'NS',
    ON: 'ON',
    PE: 'PE',
    PEI: 'PE',
    QC: 'QC',
    SK: 'SK',
  };

  function toProvinceCode(value) {
    if (value == null || value === '') return '';
    const key = String(value).trim();
    if (!key) return '';
    if (PROVINCE_CODE_ALIASES[key.toUpperCase()]) {
      return PROVINCE_CODE_ALIASES[key.toUpperCase()];
    }
    if (PROVINCE_NAME_TO_CODE[key]) return PROVINCE_NAME_TO_CODE[key];
    const lower = key.toLowerCase();
    for (const [name, code] of Object.entries(PROVINCE_NAME_TO_CODE)) {
      if (name.toLowerCase() === lower) return code;
    }
    // Already a short code-like value
    if (/^[A-Za-z]{2}$/.test(key)) return key.toUpperCase();
    return key;
  }

  function cartUpdateUrl() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (window.themeVariables &&
      window.themeVariables.routes &&
      window.themeVariables.routes.rootUrlWithoutSlash
        ? `${window.themeVariables.routes.rootUrlWithoutSlash}/`
        : '/');
    return `${root}cart/update.js`;
  }

  function cartUrl() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart.js`;
  }

  function toLocationGid(idOrGid) {
    if (!idOrGid) return '';
    const str = String(idOrGid);
    if (str.indexOf(LOCATION_GID_PREFIX) === 0) return str;
    return LOCATION_GID_PREFIX + str;
  }

  function toLocationId(idOrGid) {
    if (!idOrGid) return '';
    const str = String(idOrGid);
    if (str.indexOf(LOCATION_GID_PREFIX) === 0) {
      return str.slice(LOCATION_GID_PREFIX.length);
    }
    return str;
  }

  // `compliance.intended_province` reaches the page in two shapes: a list metafield
  // becomes a real array, and a text metafield holding JSON becomes the STRING
  // `["AB", "ON"]`. Splitting that string on commas yields `["AB"` and `"ON"]`, which
  // match no province, so every product carrying the JSON form read as unavailable
  // everywhere. Parse it first, and strip the punctuation off whatever survives.
  function normaliseProvinceList(intended) {
    if (intended == null || intended === '') return [];

    let list = intended;
    if (typeof list === 'string') {
      try {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed)) list = parsed;
      } catch (e) {
        // Not JSON: a plain comma-separated string, handled below.
      }
    }
    if (!Array.isArray(list)) list = String(list).split(',');

    return list
      .map((code) => String(code).replace(/[^A-Za-z]/g, '').toUpperCase())
      .filter(Boolean);
  }

  function isAvailableHere(item, province) {
    if (!province) return true;
    const map = window.VB_CART_COMPLIANCE || {};
    const productId = String(item.product_id);
    const intended = map[productId];

    // Soft fail: missing metafield = allowed
    if (intended == null || intended === '' || (Array.isArray(intended) && intended.length === 0)) {
      return true;
    }

    const provinceCode = toProvinceCode(province);
    const normalized = normaliseProvinceList(intended);
    if (!normalized.length) return true;
    return normalized.indexOf(String(provinceCode).trim().toUpperCase()) !== -1;
  }

  function ensureWarningModal() {
    let modal = document.getElementById('location-change-warning');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'location-change-warning';
    modal.className = 'location-change-warning';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'location-change-warning-title');
    modal.innerHTML = `
      <div class="location-change-warning__overlay" data-location-warning-dismiss></div>
      <div class="location-change-warning__dialog">
        <h2 id="location-change-warning-title" class="location-change-warning__title">Some items may not be available</h2>
        <p class="location-change-warning__copy">These products may not be sold in the province for your new store. Your cart will be kept.</p>
        <ul class="location-change-warning__list" data-location-warning-list></ul>
        <div class="location-change-warning__actions">
          <button type="button" class="button button--primary" data-location-warning-continue>Keep shopping here</button>
          <button type="button" class="button button--outline" data-location-warning-dismiss>Change store again</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    return modal;
  }

  function showLocationWarningModal(problems) {
    return new Promise((resolve) => {
      const modal = ensureWarningModal();
      const list = modal.querySelector('[data-location-warning-list]');
      list.innerHTML = '';
      problems.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item.product_title || item.title || `Product ${item.product_id}`;
        list.appendChild(li);
      });

      const finish = (proceed) => {
        modal.hidden = true;
        document.body.classList.remove('location-change-warning-open');
        modal.querySelectorAll('button, [data-location-warning-dismiss]').forEach((el) => {
          el.onclick = null;
        });
        resolve(proceed);
      };

      modal.querySelectorAll('[data-location-warning-dismiss]').forEach((el) => {
        el.onclick = () => finish(false);
      });
      modal.querySelector('[data-location-warning-continue]').onclick = () => finish(true);

      modal.hidden = false;
      document.body.classList.add('location-change-warning-open');
    });
  }

  async function updateLocationAttributes({ locationGid, locationId, province }) {
    const id = locationId || toLocationId(locationGid);
    const gid = locationGid || toLocationGid(id);
    const provinceCode = province != null && province !== '' ? toProvinceCode(province) : '';
    const attributes = {
      _selected_location_id: id ? String(id) : '',
      _selected_location: gid || '',
    };
    if (provinceCode) {
      attributes._selected_province = provinceCode;
    }

    await fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    try {
      if (id) window.localStorage.setItem('_selected_location_id', JSON.stringify(String(id)));
      if (provinceCode) window.localStorage.setItem('_selected_province', JSON.stringify(provinceCode));
    } catch (e) {
      /* ignore */
    }

    return { id, gid, province: provinceCode };
  }

  async function onLocationChange({ locationGid, locationId, province, remapFormId, skipReload }) {
    const id = locationId || toLocationId(locationGid);
    const gid = locationGid || toLocationGid(id);

    let cart = { items: [] };
    try {
      cart = await fetch(cartUrl()).then((r) => r.json());
    } catch (e) {
      cart = { items: [] };
    }

    const problems = (cart.items || []).filter((item) => !isAvailableHere(item, province));

    if (problems.length) {
      const proceed = await showLocationWarningModal(problems);
      if (!proceed) {
        return { cancelled: true, problems };
      }
    }

    const resolved = await updateLocationAttributes({ locationGid: gid, locationId: id, province });

    if (skipReload) {
      return { cancelled: false, problems, resolved };
    }

    if (remapFormId) {
      const form = document.getElementById(remapFormId);
      if (form) {
        let gidInput = form.querySelector('input[name="attributes[_selected_location]"]');
        if (!gidInput) {
          gidInput = document.createElement('input');
          gidInput.type = 'hidden';
          gidInput.name = 'attributes[_selected_location]';
          form.appendChild(gidInput);
        }
        gidInput.value = resolved.gid;

        if (resolved.province) {
          let provinceInput = form.querySelector('input[name="attributes[_selected_province]"]');
          if (!provinceInput) {
            provinceInput = document.createElement('input');
            provinceInput.type = 'hidden';
            provinceInput.name = 'attributes[_selected_province]';
            form.appendChild(provinceInput);
          }
          provinceInput.value = toProvinceCode(resolved.province);
        }

        form.submit();
        return { cancelled: false, problems, resolved, submittedForm: true };
      }
    }

    window.location.reload();
    return { cancelled: false, problems, resolved };
  }

  class LocationSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const target = e.target;
        if (!target || target.tagName !== 'SELECT') return;
        const option = target.options[target.selectedIndex];
        if (!option) return;

        onLocationChange({
          locationGid: option.dataset.locationGid || toLocationGid(option.value),
          locationId: option.value,
          province: option.dataset.province || '',
          remapFormId: option.dataset.remapFormId || this.dataset.remapFormId,
        });
      });
    }
  }

  if (!window.customElements.get('location-selector')) {
    window.customElements.define('location-selector', LocationSelector);
  }

  window.ValueBudsLocation = {
    onLocationChange,
    updateLocationAttributes,
    toLocationGid,
    toLocationId,
    toProvinceCode,
    isAvailableHere,
    showLocationWarningModal,
  };
})();
